import { LaudoData, Amostra, CalculoAvaliatario, TabelaValorTerrenoItem } from '../types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { AutomaticReportResponse, AutomaticReportAmostra } from '@/services/aiAnalysis.service';
import { SignatureData } from '../types/laudo-wizard.types';
import { fixCurrencyString, parseAndFixCurrencyValue } from './currencyUtils';
import { mapPropertyTypeNameToTipoImovel } from './tipoImovelFallback';
import { calcularEstatisticasCompletas } from './estatisticas-nbr14653';
import { deriveFormatoLaudo, normalizeFormatoLaudo } from './formatoLaudo';

// Tipos que usam Comparativo Direto (sem terreno)
const TIPOS_COMPARATIVO_DIRETO = [
  'apartamento', 'kitnet', 'loft', 'cobertura', 'flat', 'studio',
];

// Tipos que usam Método Evolutivo (com terreno)
const TIPOS_EVOLUTIVO = [
  'casa', 'sobrado', 'terreno', 'lote', 'chácara', 'fazenda', 'sítio',
];

function isComparativoDireto(tipoImovel: string): boolean {
  const tipo = tipoImovel.toLowerCase().trim();
  if (TIPOS_COMPARATIVO_DIRETO.some(t => tipo.includes(t))) return true;
  if (TIPOS_EVOLUTIVO.some(t => tipo.includes(t))) return false;
  // Default: apartamento (comparativo direto)
  return true;
}

export function transformAiReportToLaudoData(
  aiReport: AutomaticReportResponse,
  inspection: InspectionRequest,
  signature?: SignatureData
): LaudoData {
  const reportData = aiReport.reportData;
  const firstItem = inspection.items?.[0];

  if (!reportData) {
    throw new Error('Dados do laudo automático não disponíveis');
  }

  // Determinar tipo do imóvel e metodologia. Fallback: tipo cadastrado na inspeção
  // (um default fixo "Apartamento" trocava a metodologia de vistorias de casa/terreno)
  const tipoImovel = reportData.dadosImovel.tipoImovel
    || mapPropertyTypeNameToTipoImovel(firstItem?.propertyTypeName);
  const usarComparativoDireto = isComparativoDireto(tipoImovel);

  const formatarLocalData = (): string => {
    const agora = new Date();
    const diasSemana = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

    const diaSemana = diasSemana[agora.getDay()];
    const dia = agora.getDate();
    const mes = meses[agora.getMonth()];
    const ano = agora.getFullYear();

    return `${diaSemana}, ${dia} de ${mes} de ${ano}`;
  };

  // Função para calcular dados do terreno baseado nas amostras (apenas para Método Evolutivo)
  const calcularDadosTerreno = (amostrasData: AutomaticReportAmostra[]) => {
    if (usarComparativoDireto) {
      return { tabelaTerreno: [], valorUnitarioMedioTerreno: 0 };
    }

    const FATOR_OFERTA = 0.9;
    const tabelaTerreno: TabelaValorTerrenoItem[] = [];
    let somaValorUnitarioTerreno = 0;
    let countAmostrasComTerreno = 0;

    for (const amostra of amostrasData) {
      const areaTerreno = amostra.areaTerreno ?? 0;
      const areaPrivativa = amostra.areaPrivativa ?? 0;
      const valorTotal = parseAndFixCurrencyValue(amostra.valorOferta, false);
      const valorM2 = parseAndFixCurrencyValue(amostra.valorUnitarioM2, true);

      if (areaTerreno > 0 && areaPrivativa > 0 && valorTotal > 0 && valorM2 > 0) {
        const valorConstrucao = areaPrivativa * valorM2;
        const valorTerreno = Math.max(0, valorTotal - valorConstrucao);
        const valorUnitarioTerreno = valorTerreno / areaTerreno;
        const valorUnitarioFinal = valorUnitarioTerreno * FATOR_OFERTA;

        somaValorUnitarioTerreno += valorUnitarioFinal;
        countAmostrasComTerreno++;

        tabelaTerreno.push({
          amostra: amostra.idAmostra,
          tipoImovel: amostra.tipoImovel || 'N/A',
          areaTerreno: `${areaTerreno.toFixed(2).replace('.', ',')} m²`,
          padrao: amostra.padraoAcabamento || '-',
          topografia: '-',
          valorTerreno: `R$ ${valorTerreno.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          valorUnitario: `R$ ${valorUnitarioTerreno.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          indiceFiscal: '-',
          fatorOferta: FATOR_OFERTA.toFixed(2).replace('.', ','),
          fatorTopografia: '1,00',
          fat: FATOR_OFERTA.toFixed(2).replace('.', ','),
          valorUnitarioFinal: `R$ ${valorUnitarioFinal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        });
      }
    }

    const valorUnitarioMedioTerreno = countAmostrasComTerreno > 0
      ? somaValorUnitarioTerreno / countAmostrasComTerreno
      : 0;

    return { tabelaTerreno, valorUnitarioMedioTerreno };
  };

  const { tabelaTerreno, valorUnitarioMedioTerreno } = calcularDadosTerreno(reportData.amostras || []);

  // Calcular valores do terreno para o imóvel alvo (apenas Evolutivo)
  const areaTerreno = usarComparativoDireto
    ? 0
    : parseFloat(reportData.dadosImovel.areaTerreno?.replace(/[^\d,.-]/g, '').replace(',', '.') || '0');
  const valorParcialTerreno = areaTerreno * valorUnitarioMedioTerreno;

  // Extrair valores unitários (R$/m²) de cada amostra para cálculos estatísticos
  const valoresM2Amostras = (reportData.amostras || [])
    .map((a) => parseAndFixCurrencyValue(a.valorUnitarioM2, true))
    .filter((v) => v > 0);

  // Calcular estatísticas para exibição (homogeneização, limites, etc.)
  const statsReais = calcularEstatisticasCompletas(valoresM2Amostras);

  // IMPORTANTE: Usar os valores JÁ CALCULADOS pelo backend (decimal, alta precisão)
  // ao invés de recalcular no frontend (float64, perde precisão ao parsear strings formatadas)
  // Isso garante que PDF e tela de metodologia mostrem exatamente o mesmo valor
  const valorEstimadoBackend = parseAndFixCurrencyValue(reportData.avaliacaoFinal?.valorAvaliacao, false);
  const valorM2Backend = parseAndFixCurrencyValue(reportData.avaliacaoFinal?.valorM2Medio || reportData.resumoAvaliacao?.valorUnitarioFinalM2, true);
  const valorGarantiaBackend = parseAndFixCurrencyValue(reportData.avaliacaoFinal?.valorAvaliacaoGarantia, false);
  const valorLeilaoBackend = parseAndFixCurrencyValue(reportData.avaliacaoFinal?.valorGarantiaLeilao, false);

  // Usar valores do backend se disponíveis, senão recalcular
  const valorM2Real = valorM2Backend > 0 ? valorM2Backend : (statsReais.media > 0 ? statsReais.media : 0);

  const areaPrivativaStr = reportData.dadosImovel.areaPrivativa || reportData.dadosImovel.areaConstruida || '0';
  const areaPrivativa = parseFloat(areaPrivativaStr.replace(/[^\d,.-]/g, '').replace(',', '.') || '0');

  const valorParcialConstruida = areaPrivativa * valorM2Real;

  // Valor total: usar valor do backend se disponível
  const valorEstimadoCalculado = valorEstimadoBackend > 0
    ? valorEstimadoBackend
    : (usarComparativoDireto ? valorParcialConstruida : valorParcialTerreno + valorParcialConstruida);

  const valorFinalGarantia = valorGarantiaBackend > 0
    ? valorGarantiaBackend
    : Math.round(valorEstimadoCalculado / 1000) * 1000; // Arredonda ao milhar mais próximo (>= 500 sobe)
  const valorGarantiaLeilao = valorLeilaoBackend > 0
    ? valorLeilaoBackend
    : Math.floor((valorFinalGarantia * 1.2) / 1000) * 1000;

  // Nome da metodologia baseado no tipo
  const nomeMetodologia = usarComparativoDireto
    ? 'Comparativo Direto de Dados de Mercado'
    : 'Método Evolutivo';

  const amostras: Amostra[] = (reportData.amostras || []).map((amostra) => ({
    idAmostra: amostra.idAmostra,
    externalId: amostra.idAmostra || undefined,
    valorTotalAmostra: fixCurrencyString(amostra.valorOferta, false),
    endereco: amostra.endereco,
    tipoImovel: amostra.tipoImovel,
    qtdQuartos: amostra.quartos ?? 0,
    qtdBanheiros: amostra.banheiros ?? 0,
    qtdVagas: amostra.vagas ?? 0,
    areaPrivativa: amostra.areaPrivativa,
    areaTerreno: amostra.areaTerreno ?? 0,
    valorOfertaR$: fixCurrencyString(amostra.valorOferta, false),
    valorUnitarioR$M2: fixCurrencyString(amostra.valorUnitarioM2, true),
    idadeAparente: amostra.idadeAparente ?? 0,
    padraoAcabamento: amostra.padraoAcabamento || 'Normal',
    estadoConservacao: amostra.estadoConservacao || 'Bom',
    urlFonte: amostra.urlFonte || 'N/A',
    imagemUrl: amostra.imagemUrl,
  }));

  // Extrair fatores individuais do FAT
  // No AVM, o FAT geralmente é composto por fator de oferta (F1) e possivelmente vagas (F2)
  // O backend retorna apenas o FAT total, então precisamos decompor
  const FATOR_OFERTA_AVM = 0.90; // Sul/Sudeste padrão
  const tabelaCalculo: CalculoAvaliatario[] = (reportData.calculosAvaliatorios?.tabelaCalculo || []).map((calc) => {
    const fatValue = parseFloat(calc.fat?.replace(',', '.') || '1');
    // F1 = fator de oferta (0.90), restante do FAT vai para F2 (vagas)
    const fatorVagas = fatValue / FATOR_OFERTA_AVM;
    const temFatorVagas = Math.abs(fatorVagas - 1) > 0.001;

    return {
      id: calc.id,
      area: calc.area,
      valorUnit: fixCurrencyString(calc.valorUnit, true),
      F1: FATOR_OFERTA_AVM.toFixed(2).replace('.', ','),
      F2: temFatorVagas ? fatorVagas.toFixed(2).replace('.', ',') : '-',
      F3: '-',
      F4: '-',
      F5: '-',
      F6: '-',
      F7: '-',
      F8: '-',
      F9: '-',
      FAT: calc.fat,
      SAN: calc.san as 'S' | 'N',
      valorHomog: fixCurrencyString(calc.valorHomog, true),
    };
  });

  const laudoData = {
    header: {
      empresaLogo: 'bari.',
      tituloDocumento: 'LAUDO DE AVALIAÇÃO AUTOMÁTICO',
      idLaudo: reportData.header.idLaudo || `#AUTO-${inspection.proposalNumber}`,
      formatoLaudo: normalizeFormatoLaudo(reportData.header?.formatoLaudo) ?? deriveFormatoLaudo(tipoImovel),
    },

    dadosPedido: {
      solicitanteNome: reportData.dadosPedido.solicitanteNome || 'DI Documentação Imobiliária',
      propostaNumero: reportData.dadosPedido.propostaNumero || inspection.proposalNumber || 'N/A',
      dataSolicitacao: reportData.dadosPedido.dataSolicitacao || new Date(inspection.createdAt || Date.now()).toLocaleDateString('pt-BR'),
      empresaResponsavelNome: reportData.dadosPedido.empresaResponsavelNome || 'Banco Bari - Sistema de IA',
      avaliadorResponsavelNome: signature?.evaluatorName || '-',
      avaliadorCrea: signature?.creaCauNumber || '-',
      proponenteNome: reportData.dadosPedido.proponenteNome || inspection.proponents?.[0]?.name || 'Não informado',
      proponenteDocumento: reportData.dadosPedido.proponenteDocumento || inspection.proponents?.[0]?.cpfCnpj || 'Não informado',
    },

    dadosImovel: {
      enderecoRua: reportData.dadosImovel.enderecoRua || firstItem?.street || 'Não informado',
      enderecoNumero: reportData.dadosImovel.enderecoNumero || firstItem?.number || 'S/N',
      enderecoComplemento: reportData.dadosImovel.enderecoComplemento || firstItem?.complement || '',
      enderecoBairro: reportData.dadosImovel.enderecoBairro || firstItem?.district || '-',
      enderecoMunicipio: reportData.dadosImovel.enderecoMunicipio || firstItem?.city || '-',
      enderecoUf: reportData.dadosImovel.enderecoUf || firstItem?.state || '-',
      enderecoCep: reportData.dadosImovel.enderecoCep || firstItem?.cep || '-',
      tipoImovel: tipoImovel,
      matriculaNumero: reportData.dadosImovel.matriculaNumero || firstItem?.registrationNumber || 'N/A',
      matriculaRegistroImoveis: reportData.dadosImovel.matriculaRegistroImoveis || firstItem?.registryOfficeNumber || 'N/A',
      iptuNumero: reportData.dadosImovel.iptuNumero || firstItem?.iptuNumber || 'N/A',
      areaConstruida: reportData.dadosImovel.areaConstruida || '-',
      areaTerreno: reportData.dadosImovel.areaTerreno || '-',
      areaPrivativa: reportData.dadosImovel.areaPrivativa || '-',
    },

    resumoAvaliacao: {
      metodologiaAplicada: nomeMetodologia,
      grauFundamentacao: reportData.resumoAvaliacao.grauFundamentacao || calcularGrauFundamentacaoLocal(statsReais.numeroElementos),
      grauPrecisao: reportData.resumoAvaliacao.grauPrecisao || calcularGrauPrecisaoLocal(statsReais.intervaloConfiabilidade),
      areaPrivativaTotal: reportData.resumoAvaliacao.areaPrivativaTotal || reportData.dadosImovel.areaPrivativa,
      valorUnitarioFinalM2: formatarMoedaLocal(valorM2Real),
      valorFinalGarantia: formatarMoedaLocal(valorFinalGarantia),
      valorSomenteAreaAverbada: formatarMoedaLocal(valorEstimadoCalculado),
      tipoImovel: tipoImovel,
    },

    vistoriaImovel: {
      questionario: [],
    },

    amostras: amostras,

    avaliacaoMetodologia: {
      metodologia: nomeMetodologia,
      grauPrecisao: reportData.resumoAvaliacao.grauPrecisao || calcularGrauPrecisaoLocal(statsReais.intervaloConfiabilidade),
      grauFundamentacao: reportData.resumoAvaliacao.grauFundamentacao || calcularGrauFundamentacaoLocal(statsReais.numeroElementos),
    },

    calculosAvaliatorios: {
      legendaFatores: reportData.calculosAvaliatorios?.legendaFatores || 'Avaliacao realizada por modelo AVM automatizado com selecao de amostras por IA',
      tabelaCalculo: tabelaCalculo,
      fatoresAtivos: tabelaCalculo.some(r => r.F2 !== '-') ? 2 : 1,
      mediaValorUnitarioOriginal: formatarMoedaLocal(statsReais.media),
      mediaValorHomogeneizado: formatarMoedaLocal(statsReais.media),
    },

    homogeneizacao: {
      nElementos: String(statsReais.numeroElementos),
      valorUnitarioMedio: formatarMoedaLocal(statsReais.media),
      limiteInferior30: formatarMoedaLocal(statsReais.limiteInferior),
      limiteSuperior30: formatarMoedaLocal(statsReais.limiteSuperior),
    },

    tabelaValorTerreno: tabelaTerreno.length > 0 ? tabelaTerreno : undefined,

    detalhamentoValores: {
      areaTerreno: usarComparativoDireto ? '0,00 m²' : (reportData.dadosImovel.areaTerreno || '0,00 m²'),
      valorUnitarioTerreno: formatarMoedaLocal(usarComparativoDireto ? 0 : valorUnitarioMedioTerreno),
      valorParcialTerreno: formatarMoedaLocal(valorParcialTerreno),
      areaConstruida: reportData.dadosImovel.areaPrivativa || reportData.dadosImovel.areaConstruida,
      valorUnitarioConstruida: formatarMoedaLocal(valorM2Real),
      valorParcialConstruida: formatarMoedaLocal(valorParcialConstruida),
      valorTotal: formatarMoedaLocal(valorEstimadoCalculado),
    },

    quadroResumo: {
      valorAvaliacaoCalculado: formatarMoedaLocal(valorEstimadoCalculado),
    },

    avaliacaoFinal: {
      // Exibição arredondada ao milhar mais próximo (cálculos internos seguem com o valor cheio)
      valorAvaliacao: formatarMoedaLocal(Math.round(valorEstimadoCalculado / 1000) * 1000),
      valorAvaliacaoGarantia: formatarMoedaLocal(valorFinalGarantia),
      valorGarantiaLeilao: formatarMoedaLocal(valorGarantiaLeilao),
    },

    comentarios: reportData.observacoes || 'Laudo gerado automaticamente por Inteligência Artificial (AVM - Automated Valuation Model). ' +
      `Metodologia: ${reportData.metodologia.nome}. ` +
      `Código do parecer: ${reportData.metodologia.codigoParecer || 'N/A'}. ` +
      `Amostras utilizadas: ${reportData.metodologia.amostrasUtilizadas}.`,

    relatorioFotografico: {
      listaFotos: [],
    },

    responsavelLaudo: {
      localData: formatarLocalData(),
      imagemAssinaturaCarimbo: signature?.signatureImage || '',
      nomeResponsavel: signature?.evaluatorName || '',
      creaResponsavel: signature?.creaCauNumber || '',
    },

    footer: {
      versaoDocumento: '1.0.0-AUTO',
      poweredBy: 'nextage.com + IA',
      paginacao: '1 | 1',
    },
  } as LaudoData;

  return laudoData;
}

function formatarMoedaLocal(valor: number): string {
  if (!valor || isNaN(valor)) return 'R$ 0,00';
  return `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function calcularGrauFundamentacaoLocal(numeroAmostras: number): string {
  if (numeroAmostras >= 6) return 'Grau de Fundamentação III';
  if (numeroAmostras >= 4) return 'Grau de Fundamentação II';
  if (numeroAmostras >= 3) return 'Grau de Fundamentação I';
  return 'Grau de Fundamentação insuficiente';
}

function calcularGrauPrecisaoLocal(intervaloConfiabilidade: number): string {
  const amplitudePercentual = intervaloConfiabilidade * 100;
  if (amplitudePercentual <= 15) return 'Grau de Precisão III';
  if (amplitudePercentual <= 30) return 'Grau de Precisão II';
  if (amplitudePercentual <= 40) return 'Grau de Precisão I';
  return 'Grau de Precisão insuficiente';
}
